import { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { Bold, Italic, Underline, Heading2, Heading3, List, Link2, Image as ImageIcon, Code2, FileCode2, Upload, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { sanitizeHtml } from "@/lib/sanitize";
import { pipeHtmlToBullets } from "@/lib/pipeText";

const Btn = ({ title, onClick, children, active }) => (
  <button type="button" title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
    className={`p-1.5 rounded hover:bg-white/10 transition-colors ${active ? "bg-white/15 text-white" : "text-zinc-400"}`}>{children}</button>
);

export const RichTextEditor = ({ value, onChange, placeholder = "Write here...", testid = "rich-editor" }) => {
  const ref = useRef(null);
  const [htmlMode, setHtmlMode] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!htmlMode && ref.current && ref.current.innerHTML !== (value || "")) ref.current.innerHTML = pipeHtmlToBullets(sanitizeHtml(value));
  }, [htmlMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const emit = () => onChange(ref.current ? ref.current.innerHTML : "");
  const cmd = (c, v = null) => { ref.current?.focus(); document.execCommand(c, false, v); emit(); };
  const insertHtml = (html) => { ref.current?.focus(); document.execCommand("insertHTML", false, html); emit(); };
  const addLink = () => { const url = window.prompt("Link URL (https://...)"); if (url) cmd("createLink", url); };
  const addImageUrl = () => { const url = window.prompt("Image URL (https://...)"); if (url) insertHtml(`<img src="${url}" alt="" /><p><br/></p>`); };
  const addCode = () => insertHtml("<pre><code>// your code here</code></pre><p><br/></p>");
  const convertPipes = () => {
    if (!ref.current) return;
    const converted = pipeHtmlToBullets(ref.current.innerHTML);
    if (converted !== ref.current.innerHTML) { ref.current.innerHTML = converted; emit(); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      insertHtml(`<img src="${data.url}" alt="" /><p><br/></p>`);
      toast.success("Image inserted");
    } catch (err) { toast.error(errMsg(err)); }
    setUploading(false);
    e.target.value = "";
  };

  return (
    <div className="border border-white/10 rounded-lg bg-[#0a0a0a] overflow-hidden focus-within:border-rose-500" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-white/10 px-2 py-1.5 bg-[#111]">
        {!htmlMode && (<>
          <Btn title="Bold" onClick={() => cmd("bold")}><Bold size={14} /></Btn>
          <Btn title="Italic" onClick={() => cmd("italic")}><Italic size={14} /></Btn>
          <Btn title="Underline" onClick={() => cmd("underline")}><Underline size={14} /></Btn>
          <span className="w-px h-5 bg-white/10 mx-1" />
          <Btn title="Heading" onClick={() => cmd("formatBlock", "<h2>")}><Heading2 size={14} /></Btn>
          <Btn title="Subheading" onClick={() => cmd("formatBlock", "<h3>")}><Heading3 size={14} /></Btn>
          <Btn title="Bullet list" onClick={() => cmd("insertUnorderedList")}><List size={14} /></Btn>
          <span className="w-px h-5 bg-white/10 mx-1" />
          <Btn title="Insert link" onClick={addLink}><Link2 size={14} /></Btn>
          <Btn title="Image by URL" onClick={addImageUrl}><ImageIcon size={14} /></Btn>
          <label className="p-1.5 rounded hover:bg-white/10 text-zinc-400 cursor-pointer" title="Upload image">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`${testid}-upload`} />
          </label>
          <Btn title="Code block" onClick={addCode}><Code2 size={14} /></Btn>
        </>)}
        <span className="ml-auto" />
        <Btn title={htmlMode ? "Back to visual editor" : "Edit HTML source (paste embeds/code)"} onClick={() => setHtmlMode(!htmlMode)} active={htmlMode}><FileCode2 size={14} /></Btn>
      </div>
      {htmlMode ? (
        <textarea rows={10} className="w-full bg-transparent px-3 py-2 text-xs font-mono outline-none text-emerald-200 resize-y"
          value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder="<p>Raw HTML — embeds, iframes and code welcome</p>" data-testid={`${testid}-html`} />
      ) : (
        <div ref={ref} contentEditable suppressContentEditableWarning onInput={emit} onBlur={convertPipes}
          className="rich-content min-h-[180px] max-h-[420px] overflow-y-auto px-3 py-2.5 text-sm outline-none text-zinc-200"
          data-placeholder={placeholder} data-testid={`${testid}-area`} />
      )}
    </div>
  );
};
